<template>
  <section class="]">
    <div
      class="px-4 mx-auto max-w-3xl lg:px-0"
      :class="[
        paddingTop === 'reduced' ? 'pt-6 lg:pt-6' : 'pt-12 lg:pt-16',
        paddingBottom === 'reduced' ? 'pb-6 lg:pb-6' : 'pb-12 lg:pb-16'
      ]"
    >
      <div class="flex flex-col gap-4">
        <div class="flex gap-3 items-center md:gap-5">
          <div class="relative shrink-0 size-9">
            <span
              class="absolute inset-0 rounded-full red-flag-badge-pulse bg-[#F1574B]"
              aria-hidden="true"
            />
            <div class="flex relative z-10 justify-center items-center text-base leading-7 text-white rounded-full size-9 bg-[#F1574B] font-ww-regular">
              {{ badgeNumber }}
            </div>
          </div>
          <span
            class="text-xs tracking-wider leading-5 uppercase text-[#F1574B] font-ww-regular"
            v-html="badgeText"
          />
        </div>

        <h2
          class="text-2xl font-bold leading-tight sm:text-3xl lg:text-34 lg:leading-42 font-ww-bold text-[#3B2314]"
          v-html="title"
        />

        <div class="flex flex-col gap-6">
          <div
            class="pt-1 text-17 leading-6 text-[#4A4A4A] font-ww-regular [&_p+p]:mt-5"
            v-html="introContent"
          />

          <div class="flex flex-col gap-4">
            <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div
                v-for="(habit, index) in habits"
                :key="index"
                class="flex flex-col gap-2.5 items-center p-4 rounded-md border border-[#E0D5C5] bg-[#FAF6F0]"
              >
                <div class="shrink-0 size-[35px]">
                  <img
                    v-if="habit.iconSrc"
                    :src="habit.iconSrc"
                    :alt="habit.iconAlt"
                    width="35"
                    height="35"
                    loading="lazy"
                    class="object-contain size-[35px]"
                  />
                  <div
                    v-else
                    class="size-[35px] rounded bg-[#2A2A2A]"
                    aria-hidden="true"
                  />
                </div>
                <p
                  class="text-17 leading-6 text-center text-[#4A4A4A] font-ww-regular"
                  v-html="habit.text"
                />
              </div>
            </div>

            <div
              v-if="bannerContent"
              class="flex flex-col gap-3.5 items-center p-4 rounded-md border border-[#E0D5C5] bg-[#FAF6F0]"
            >
              <div class="shrink-0 size-[35px]">
                <img
                  v-if="bannerIconSrc"
                  :src="bannerIconSrc"
                  :alt="bannerIconAlt"
                  width="35"
                  height="35"
                  loading="lazy"
                  class="object-contain size-[35px]"
                />
                <div
                  v-else
                  class="size-[35px] rounded bg-[#2A2A2A]"
                  aria-hidden="true"
                />
              </div>
              <div
                class="text-17 leading-6 text-center text-[#4A4A4A] font-ww-regular [&_p+p]:mt-0"
                v-html="bannerContent"
              />
            </div>
          </div>

          <div
            v-if="transitionContent"
            class="text-17 leading-6 text-[#4A4A4A] font-ww-regular [&_u]:underline"
            v-html="transitionContent"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
const finalWordHabitsDefault = [
  {
    iconSrc: '/assets/images/final-word-habit-walk.svg',
    iconAlt: 'Walking icon',
    text: 'A daily walk.'
  },
  {
    iconSrc: '/assets/images/final-word-habit-nutrition.svg',
    iconAlt: 'Nutrition icon',
    text: 'A nutrient-dense meal.'
  },
  {
    iconSrc: '/assets/images/final-word-habit-mindset.svg',
    iconAlt: 'Mindset icon',
    text: 'A positive mindset.'
  },
  {
    iconSrc: '/assets/images/final-word-habit-sleep.svg',
    iconAlt: 'Sleep icon',
    text: "A good night's sleep."
  }
]
</script>

<script setup>
defineProps({
  badgeNumber: {
    type: String,
    default: '6'
  },
  badgeText: {
    type: String,
    default: 'Red Flag No. 6'
  },
  title: {
    type: String,
    default: "Final Word: Men's Health Isn't Complicated, It's Just Ignored"
  },
  introContent: {
    type: String,
    default: `<p>Men's health doesn't decline overnight. It declines one small habit at a time.</p>
<p>But the opposite is also true:</p>
<p>Men's health improves one small habit at a time.</p>`
  },
  habits: {
    type: Array,
    default: () => finalWordHabitsDefault
  },
  bannerIconSrc: {
    type: String,
    default: '/assets/images/final-word-banner-coffee.svg'
  },
  bannerIconAlt: {
    type: String,
    default: 'Coffee cup icon'
  },
  bannerContent: {
    type: String,
    default: `<p>And a clean, functional cup of coffee that supports your heart, brain, gut, and energy.</p>
<p>Small Changes, Big Results: The Simple Path to a Healthier You</p>`
  },
  transitionContent: {
    type: String,
    default: `<p>Wow! We've covered a lot of info, but before we close things out, I wanted to leave you with a few powerful tools which can support <u>each and every area of concern</u> we've touched on today.</p>`
  },
  paddingTop: {
    type: String,
    default: 'reduced',
    validator: (value) => ['default', 'reduced'].includes(value)
  },
  paddingBottom: {
    type: String,
    default: 'reduced',
    validator: (value) => ['default', 'reduced'].includes(value)
  }
})

defineBuilderComponent({
  friendlyName: 'Content - Final Word Intro',
  inputs: [
    {
      name: 'badgeNumber',
      type: 'text',
      defaultValue: '6'
    },
    {
      name: 'badgeText',
      type: 'text',
      defaultValue: 'Red Flag No. 6'
    },
    {
      name: 'title',
      type: 'richText',
      defaultValue: "Final Word: Men's Health Isn't Complicated, It's Just Ignored"
    },
    {
      name: 'introContent',
      type: 'richText',
      defaultValue: `<p>Men's health doesn't decline overnight. It declines one small habit at a time.</p>
<p>But the opposite is also true:</p>
<p>Men's health improves one small habit at a time.</p>`
    },
    {
      name: 'habits',
      type: 'list',
      defaultValue: [
        {
          iconSrc: '/assets/images/final-word-habit-walk.svg',
          iconAlt: 'Walking icon',
          text: 'A daily walk.'
        },
        {
          iconSrc: '/assets/images/final-word-habit-nutrition.svg',
          iconAlt: 'Nutrition icon',
          text: 'A nutrient-dense meal.'
        },
        {
          iconSrc: '/assets/images/final-word-habit-mindset.svg',
          iconAlt: 'Mindset icon',
          text: 'A positive mindset.'
        },
        {
          iconSrc: '/assets/images/final-word-habit-sleep.svg',
          iconAlt: 'Sleep icon',
          text: "A good night's sleep."
        }
      ],
      subFields: [
        {
          name: 'iconSrc',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'svg'],
          defaultValue: ''
        },
        {
          name: 'iconAlt',
          type: 'text',
          defaultValue: ''
        },
        {
          name: 'text',
          type: 'text',
          defaultValue: ''
        }
      ]
    },
    {
      name: 'bannerIconSrc',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'svg'],
      defaultValue: '/assets/images/final-word-banner-coffee.svg'
    },
    {
      name: 'bannerIconAlt',
      type: 'text',
      defaultValue: 'Coffee cup icon'
    },
    {
      name: 'bannerContent',
      type: 'richText',
      defaultValue: `<p>And a clean, functional cup of coffee that supports your heart, brain, gut, and energy.</p>
<p>Small Changes, Big Results: The Simple Path to a Healthier You</p>`
    },
    {
      name: 'transitionContent',
      type: 'richText',
      defaultValue: `<p>Wow! We've covered a lot of info, but before we close things out, I wanted to leave you with a few powerful tools which can support <u>each and every area of concern</u> we've touched on today.</p>`
    },
    {
      name: 'paddingTop',
      type: 'text',
      enum: ['default', 'reduced'],
      defaultValue: 'reduced'
    },
    {
      name: 'paddingBottom',
      type: 'text',
      enum: ['default', 'reduced'],
      defaultValue: 'reduced'
    }
  ]
})
</script>
